import { PDFDocument } from 'pdf-lib';
import html2canvas from 'html2canvas';

export async function generateTransactionPDFFromHTML(invoiceRef, transactionId) {
  try {
    if (!invoiceRef.current) {
      throw new Error("Invoice reference not found");
    }
    // Wait for any remaining renders to complete
    await new Promise(resolve => setTimeout(resolve, 500));

    // Capture the invoice component as canvas with very permissive settings
    const canvas = await html2canvas(invoiceRef.current, {
      scale: 2,
      useCORS: true,       // allow cross-origin images
      allowTaint: false, // Allow tainted canvas
      backgroundColor: '#ffffff',
      width: invoiceRef.current.offsetWidth,
      height: invoiceRef.current.offsetHeight,
      scrollX: 0,
      scrollY: 0,
      logging: true, // Enable logging to see what's happening
      imageTimeout: 0, // Don't wait for images since we've handled them
      ignoreElements: (element) => {
        // Skip any elements that might cause issues
        return element.tagName === 'IMG' && element.src.startsWith('http');
      },
      onclone: (clonedDoc) => {
        // Remove any problematic img elements that might still exist
        const images = clonedDoc.querySelectorAll('img[src^="http"]');
        images.forEach(img => {
          img.style.display = 'none'; // Hide instead of remove to maintain layout
        });
      }
    });

    // Create PDF document
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([595.28, 841.89]); // A4 size in points

    // Convert canvas to image
    const imgData = canvas.toDataURL('image/png');
    const imgBytes = await fetch(imgData).then((res) => res.arrayBuffer());
    const image = await pdfDoc.embedPng(imgBytes);

    // Calculate dimensions to fit A4
    const { width: pageWidth, height: pageHeight } = page.getSize();
    
    // Scale the image to fit the page with some margin
    const margin = 40;
    const maxWidth = pageWidth - (margin * 2);
    const maxHeight = pageHeight - (margin * 2);
    
    const scale = Math.min(
      maxWidth / canvas.width,
      maxHeight / canvas.height
    );
    
    const scaledWidth = canvas.width * scale;
    const scaledHeight = canvas.height * scale;
    
    // Center the image on the page
    const x = (pageWidth - scaledWidth) / 2;
    const y = (pageHeight - scaledHeight) / 2;

    page.drawImage(image, {
      x,
      y,
      width: scaledWidth,
      height: scaledHeight,
    });

    // Save and download
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `invoice_${transactionId || new Date().getTime()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return pdfBytes;
  } catch (error) {
    console.error('Error generating PDF from HTML:', error);
    throw error;
  }
}
